import { useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * WHERE THE SCENE REVIEW IS, AS AN ADDRESS.
 *
 * The filter, the search and the scene being read are the review's state, and
 * "scene 41 of the flagged ones" is a thing somebody sends a colleague — so all
 * three live in the URL (`?filter=&q=&scene=`) wherever a router is mounted.
 * They are written in ONE navigation: "Review flagged scenes" changes the
 * filter and the scene together, and two writes in one tick would let the
 * second overwrite the first.
 *
 * Without a router (a render test, an embed) the same state lives in the
 * component, honouring the props a caller passes.
 */
export type SceneFilter = 'ALL' | 'TO_REVIEW' | 'CONFIRMED' | 'NEEDS_ATTENTION' | 'EXCLUDED';

export interface SceneAddress {
  readonly filter: SceneFilter;
  readonly q: string;
  /** The scene somebody opened; `null` lets the workspace pick the first one shown. */
  readonly sceneId: string | null;
}

export type SetSceneAddress = (next: Partial<SceneAddress>) => void;

const FILTERS: readonly SceneFilter[] = ['ALL', 'TO_REVIEW', 'CONFIRMED', 'NEEDS_ATTENTION', 'EXCLUDED'];

/** `?filter=`; anything unknown (an old bookmark's location filter) is the whole list. */
export function parseSceneFilter(value: string | null): SceneFilter {
  return FILTERS.find((f) => f === value) ?? 'ALL';
}

/** A scene id is a ULID or a short fixture id; anything else in `?scene=` is ignored. */
function parseSceneId(value: string | null): string | null {
  return value !== null && /^[A-Za-z0-9_-]{1,40}$/.test(value) ? value : null;
}

/** The address kept in the URL, merged into whatever else the query holds (`?discuss=`…). */
export function RoutedSceneAddress({ children }: { children: (address: SceneAddress, set: SetSceneAddress) => ReactNode }) {
  const [params, setParams] = useSearchParams();
  const address: SceneAddress = {
    filter: parseSceneFilter(params.get('filter')),
    q: params.get('q') ?? '',
    sceneId: parseSceneId(params.get('scene')),
  };

  const set: SetSceneAddress = (patch) => {
    const merged = { ...address, ...patch };
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (merged.filter === 'ALL') next.delete('filter');
        else next.set('filter', merged.filter);
        if (merged.q === '') next.delete('q');
        else next.set('q', merged.q);
        if (merged.sceneId === null) next.delete('scene');
        else next.set('scene', merged.sceneId);
        return next;
      },
      { replace: true },
    );
  };

  return <>{children(address, set)}</>;
}

/**
 * The address kept in the component. A `filter` or `query` the caller passes
 * wins over the component's own, and each change is reported to it.
 */
export function LocalSceneAddress({
  filter,
  query,
  onFilterChange,
  onQueryChange,
  children,
}: {
  filter?: SceneFilter;
  query?: string;
  onFilterChange?: (filter: SceneFilter) => void;
  onQueryChange?: (query: string) => void;
  children: (address: SceneAddress, set: SetSceneAddress) => ReactNode;
}) {
  const [own, setOwn] = useState<SceneAddress>({ filter: 'ALL', q: '', sceneId: null });
  const address: SceneAddress = { filter: filter ?? own.filter, q: query ?? own.q, sceneId: own.sceneId };

  const set: SetSceneAddress = (patch) => {
    setOwn((cur) => ({ ...cur, ...patch }));
    if (patch.filter !== undefined && patch.filter !== address.filter) onFilterChange?.(patch.filter);
    if (patch.q !== undefined && patch.q !== address.q) onQueryChange?.(patch.q);
  };

  return <>{children(address, set)}</>;
}
